/*
This file is located in the main branch
Last time the file was updated (UTC): [11/6/2024 4:40:59AM]
Permissions to update outside the main branch: [false]
*/

import {
    Filter,
    getFilterElementsDefault,
    useWorkplaceState,
    WorkplaceBtnActions,
} from '@finbeat/react-toolkit';
import { faBoltLightning } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    addUser,
    changeUser,
    deleteUser,
    getUser,
    getUsersExcel,
    getUsersList,
} from 'axios/userApi';
import { BOWorkplace } from 'base-components/BOWorkplace/BOWorkplace';
import ButtonExcelFileUpload from 'components/ExcelFileUpload/ButtonExcelFileUpload';
import useApi, { getRefreshedAuth } from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import {
    UserAdd,
    UserChange,
    UserDelete,
    UserFilter as UserFilterModel,
    UserItem,
    UserTableItem,
} from 'models/api/users';
import { useCallback, useMemo, useState } from 'react';
import { FormatterProps } from 'react-data-grid';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { checkboxFormatter, phoneNumberFormatter } from 'utils/columnFormatters';
import { UploadResult } from 'utils/fileUploadNotificator';
import UserAddButton from './Buttons/UserAddButton';
import UserChangePasswordBtn from './Buttons/UserChangePasswordBtn';
import UserDeleteBtn from './Buttons/UserDeleteBtn';
import UserRestoreBtn from './Buttons/UserRestoreButton';
import UserChangeModal from './Modal/UserChangeModal';
import UserFilter from './UserFilter';
import './Users.scss';

type UsersFormatterProps = FormatterProps<UserTableItem, UserTableItem[]>;

const Users = () => {
    const { t } = useTranslation();
    const api = useApi();
    const queryClient = useQueryClient();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const { auth, setAuth, hasRole } = useAuth();

    const uploadCallback = (res: UploadResult) => {
        (res === 'success' || res === 'warning') && queryClient.invalidateQueries([['users']]);
    };

    const buttons = useMemo(() => {
        const result = [];

        if (hasRole('accountsCreate')) {
            result.push(<UserAddButton key="1" showModal={() => setIsModalVisible(true)} />);
        }

        if (hasRole('accountsUpload')) {
            result.push(
                <ButtonExcelFileUpload key="2" controller="accounts" callback={uploadCallback} />
            );
        }

        return result;
    }, [hasRole]);

    const actionsFormatter = useCallback(
        ({ row }: UsersFormatterProps) => (
            <WorkplaceBtnActions>
                {hasRole('accountsDelete') && row.isActive && (
                    <UserDeleteBtn
                        key={`delete-btn-${row.id}`}
                        id={row.id}
                        isExternalUser={row.isExternalUser}
                    />
                )}
                {hasRole('accountsRestore') && !row.isActive && (
                    <UserRestoreBtn
                        key={`restore-btn-${row.id}`}
                        id={row.id}
                        isExternalUser={row.isExternalUser}
                    />
                )}
                {hasRole('accountsChangeOtherPassword') && row.id != null && (
                    <UserChangePasswordBtn key={`change-password-btn-${row.id}`} id={row.id} />
                )}
            </WorkplaceBtnActions>
        ),
        [hasRole]
    );

    return (
        <BOWorkplace<
            UserTableItem,
            UserItem,
            Filter<UserFilterModel>,
            UserChange,
            UserAdd,
            UserDelete
        >
            testId="users"
            pageTitle="BackOfficeUsers"
            filterParams={{
                id: '',
                fullname: '',
                userName: '',
                showInactive: false,
                showTechnicalUser: false,
            }}
            initColumns={[
                {
                    key: 'id',
                    name: <Trans i18nKey="BackOfficeId" />,
                    width: 70,
                    minWidth: 70,
                },
                { key: 'userName', name: <Trans i18nKey={'BackOfficeLogin'} /> },
                {
                    key: 'isActive',
                    name: <Trans i18nKey="BackOfficeIsActive" />,
                    cellClass: 'cell-centered',
                    headerCellClass: 'cell-centered',
                    width: 50,
                    minWidth: 50,

                    resizable: false,
                    notHidden: true,
                    editable: true,
                    formatter: checkboxFormatter('isActive'),
                },
                {
                    key: 'isTechnicalUser',
                    name: <Trans i18nKey="BackOfficeIsTechnicalUser" />,
                    cellClass: 'cell-centered',
                    headerCellClass: 'cell-centered',
                    width: 50,
                    minWidth: 50,
                    resizable: false,
                    notHidden: true,
                    editable: true,
                    formatter: checkboxFormatter('isTechnicalUser'),
                },
                { key: 'firstName', name: <Trans i18nKey={'BackOfficeFirstName'} /> },
                { key: 'lastName', name: <Trans i18nKey={'BackOfficeLastName'} /> },
                { key: 'middleName', name: <Trans i18nKey={'BackOfficeMiddleName'} /> },
                {
                    key: 'phoneNumber',
                    name: <Trans i18nKey={'BackOfficePhone'} />,
                    formatter: phoneNumberFormatter('phoneNumber'),
                },
                { key: 'email', name: <Trans i18nKey={'BackOfficeEmail'} /> },
                {
                    key: 'actions',
                    name: <FontAwesomeIcon icon={faBoltLightning} />,
                    cellClass: 'cell-centered no-change-modal',
                    headerCellClass: 'cell-centered',
                    width: 70,
                    minWidth: 70,
                    resizable: false,
                    clickDisable: true,
                    visible:
                        hasRole('accountsDelete') ||
                        hasRole('accountsRestore') ||
                        hasRole('accountsChangeOtherPassword'),
                    notHidden: true,
                    formatter: actionsFormatter,
                },
            ]}
            storagePath="brands/users"
            getFilterElements={getFilterElementsDefault([
                { id: 'id', name: t('BackOfficeId') },
                { id: 'userName', name: t('BackOfficeUsername') },
                { id: 'fullname', name: t('BackOfficeFio') },
                {
                    id: 'showInactive',
                    formatter: (val) => {
                        if (val) {
                            return t('BackOfficeShowInactive');
                        }
                    },
                },
            ])}
            showModal={hasRole('accountsGetById') ? () => setIsModalVisible(true) : undefined}
            buttons={buttons}
            filter={<UserFilter />}
            modal={{
                component: isModalVisible && (
                    <UserChangeModal isOpen={isModalVisible} toggle={setIsModalVisible} />
                ),
                fetchCurrentItemQueryFn: (id) => getUser(api, id),
                changeMutationFn: (item) =>
                    changeUser(api, item).then(async (i) => {
                        if (item.id === auth?.userId) {
                            const newAuth = await getRefreshedAuth(i.config);
                            setAuth(newAuth);
                        }
                    }),
                addMutationFn: (item) => addUser(api, item),
            }}
            deleteMutationFn={({ id, crmSync }) => deleteUser(api, id, crmSync)}
            fetchQueryFn={(filter) => getUsersList(api, filter)}
            queryKey={['users']}
            queryClient={queryClient}
            fetchExcelQueryFn={(filter) => getUsersExcel(api, filter)}
            showExcelBtn={hasRole('accountsExcel')}
        />
    );
};

const useUserWorkplaceState = () =>
    useWorkplaceState<UserTableItem, UserItem, UserChange, UserAdd, UserDelete>();

export default Users;
export { useUserWorkplaceState };
